'use strict'

const PORTAL_LOADING_ROOT_SELECTOR = '[data-portal-loading-root]'
const PORTAL_LOADING_STACK_SELECTOR = '[data-portal-loading-stack]'
const PORTAL_LOADING_TEMPLATE_SELECTOR = '[data-tpl-portal-loading-item]'
const PORTAL_LOADING_ITEM_SELECTOR = '[data-portal-loading-item]'
const PORTAL_LOADING_TITLE_SELECTOR = '[data-portal-loading-title]'
const PORTAL_LOADING_STATUS_SELECTOR = '[data-portal-loading-status]'
const PORTAL_LOADING_PERCENT_SELECTOR = '[data-portal-loading-percent]'
const PORTAL_LOADING_FILL_SELECTOR = '[data-portal-loading-fill]'
const PORTAL_LOADING_MIN_VISIBLE_MS = 1000
const PORTAL_LOADING_SUCCESS_HIDE_DELAY_MS = 1200
const PORTAL_LOADING_ERROR_HIDE_DELAY_MS = 2600

let portalLoadingRefs = null
let portalLoadingSeq = 0
const portalLoadingTasks = new Map()

const fnGetPortalLoadingRefs = function () {
  if (portalLoadingRefs) return portalLoadingRefs
  if (typeof document === 'undefined') return null
  const root = document.querySelector(PORTAL_LOADING_ROOT_SELECTOR)
  const stack = document.querySelector(PORTAL_LOADING_STACK_SELECTOR)
  const template = document.querySelector(PORTAL_LOADING_TEMPLATE_SELECTOR)
  if (!root || !stack || !template) return null
  portalLoadingRefs = {
    root: root,
    stack: stack,
    template: template
  }
  return portalLoadingRefs
}

const fnSyncPortalLoadingRootState = function () {
  const refs = fnGetPortalLoadingRefs()
  if (!refs || !refs.root) return
  const count = portalLoadingTasks.size
  refs.root.setAttribute('data-portal-loading-empty', count > 0 ? '0' : '1')
  refs.root.setAttribute('data-portal-loading-count', String(count))
}

const fnClonePortalLoadingItem = function () {
  const refs = fnGetPortalLoadingRefs()
  if (!refs || !refs.template) return null
  if (refs.template.content && refs.template.content.firstElementChild) {
    return refs.template.content.firstElementChild.cloneNode(true)
  }
  const firstChild = refs.template.firstElementChild
  return firstChild ? firstChild.cloneNode(true) : null
}

const fnNormalizePortalLoadingText = function (value) {
  return String(value === undefined || value === null ? '' : value).trim()
}

const fnNormalizePortalLoadingProgress = function (value) {
  const progress = Number(value)
  if (!Number.isFinite(progress)) return null
  return Math.max(0, Math.min(100, progress))
}

const fnClearPortalLoadingRemovalTimer = function (task) {
  if (!task || !task.removeTimer) return
  clearTimeout(task.removeTimer)
  task.removeTimer = null
}

const fnRemovePortalLoadingTask = function (taskId) {
  const task = portalLoadingTasks.get(taskId)
  if (!task) return
  fnClearPortalLoadingRemovalTimer(task)
  if (task.node && task.node.parentNode) {
    task.node.parentNode.removeChild(task.node)
  }
  portalLoadingTasks.delete(taskId)
  fnSyncPortalLoadingRootState()
}

const fnSchedulePortalLoadingRemoval = function (task, delayMs) {
  if (!task) return
  fnClearPortalLoadingRemovalTimer(task)
  const minVisibleRemaining = Math.max(0, (Number(task.createdAt) || 0) + PORTAL_LOADING_MIN_VISIBLE_MS - Date.now())
  const delay = Math.max(0, Number(delayMs) || 0, minVisibleRemaining)
  task.removeTimer = setTimeout(function () {
    fnRemovePortalLoadingTask(task.id)
  }, delay)
}

const fnRenderPortalLoadingTask = function (task) {
  if (!task || !task.node) return
  const titleEl = task.node.querySelector(PORTAL_LOADING_TITLE_SELECTOR)
  const statusEl = task.node.querySelector(PORTAL_LOADING_STATUS_SELECTOR)
  const percentEl = task.node.querySelector(PORTAL_LOADING_PERCENT_SELECTOR)
  const fillEl = task.node.querySelector(PORTAL_LOADING_FILL_SELECTOR)
  const statusText = fnNormalizePortalLoadingText(task.status)
  const titleText = fnNormalizePortalLoadingText(task.title)
  const progress = fnNormalizePortalLoadingProgress(task.progress)
  const mode = String(task.mode || (progress === null ? 'indeterminate' : 'determinate')).trim().toLowerCase() === 'determinate'
    ? 'determinate'
    : 'indeterminate'

  task.node.setAttribute('data-portal-loading-state', String(task.state || 'running'))
  task.node.setAttribute('data-portal-loading-mode', mode)

  if (titleEl) titleEl.textContent = titleText
  if (statusEl) {
    statusEl.textContent = statusText
    statusEl.setAttribute('data-portal-loading-empty', statusText ? '0' : '1')
  }
  if (percentEl) {
    percentEl.textContent = (mode === 'determinate' && progress !== null) ? (Math.round(progress) + '%') : ''
    percentEl.setAttribute('data-portal-loading-empty', percentEl.textContent ? '0' : '1')
  }
  if (fillEl) {
    if (mode === 'determinate' && progress !== null) {
      fillEl.style.width = progress + '%'
    } else {
      fillEl.style.width = ''
    }
  }
}

const fnPatchPortalLoadingTask = function (task, patch) {
  if (!task || !patch || typeof patch !== 'object') return
  if (Object.prototype.hasOwnProperty.call(patch, 'title')) {
    task.title = fnNormalizePortalLoadingText(patch.title)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'status')) {
    task.status = fnNormalizePortalLoadingText(patch.status)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'progress')) {
    task.progress = fnNormalizePortalLoadingProgress(patch.progress)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'mode')) {
    task.mode = String(patch.mode || '').trim().toLowerCase() || task.mode
  } else if (Object.prototype.hasOwnProperty.call(patch, 'progress')) {
    task.mode = (task.progress === null) ? 'indeterminate' : 'determinate'
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'state')) {
    task.state = String(patch.state || '').trim().toLowerCase() || task.state
  }
  fnRenderPortalLoadingTask(task)
}

const fnCreatePortalLoadingController = function (taskId) {
  return {
    id: taskId,
    update: function (patch) {
      const task = portalLoadingTasks.get(taskId)
      if (!task) return this
      fnClearPortalLoadingRemovalTimer(task)
      fnPatchPortalLoadingTask(task, patch)
      return this
    },
    complete: function (patch) {
      const task = portalLoadingTasks.get(taskId)
      if (!task) return this
      const nextPatch = Object.assign({
        state: 'success',
        mode: 'determinate',
        progress: Number.isFinite(task.progress) ? task.progress : 100
      }, patch || {})
      fnPatchPortalLoadingTask(task, nextPatch)
      fnSchedulePortalLoadingRemoval(task, PORTAL_LOADING_SUCCESS_HIDE_DELAY_MS)
      return this
    },
    fail: function (patch) {
      const task = portalLoadingTasks.get(taskId)
      if (!task) return this
      const nextPatch = Object.assign({
        state: 'error'
      }, patch || {})
      fnPatchPortalLoadingTask(task, nextPatch)
      fnSchedulePortalLoadingRemoval(task, PORTAL_LOADING_ERROR_HIDE_DELAY_MS)
      return this
    },
    remove: function () {
      const task = portalLoadingTasks.get(taskId)
      if (!task) return this
      fnSchedulePortalLoadingRemoval(task, 0)
      return this
    }
  }
}

const startPortalLoadingTask = function (options) {
  const refs = fnGetPortalLoadingRefs()
  const taskId = String((options && options.id) || ('portal-loading-' + (++portalLoadingSeq)))
  if (!refs || !refs.stack) {
    return {
      id: taskId,
      update: function () { return this },
      complete: function () { return this },
      fail: function () { return this },
      remove: function () { return this }
    }
  }
  const existingTask = portalLoadingTasks.get(taskId)
  if (existingTask) {
    fnClearPortalLoadingRemovalTimer(existingTask)
    fnPatchPortalLoadingTask(existingTask, options || {})
    return fnCreatePortalLoadingController(taskId)
  }
  const node = fnClonePortalLoadingItem()
  if (!node) {
    return {
      id: taskId,
      update: function () { return this },
      complete: function () { return this },
      fail: function () { return this },
      remove: function () { return this }
    }
  }
  const task = {
    id: taskId,
    node: node,
    createdAt: Date.now(),
    title: '',
    status: '',
    progress: null,
    mode: 'indeterminate',
    state: 'running',
    removeTimer: null
  }
  refs.stack.appendChild(node)
  portalLoadingTasks.set(taskId, task)
  fnPatchPortalLoadingTask(task, options || {})
  fnSyncPortalLoadingRootState()
  return fnCreatePortalLoadingController(taskId)
}

const initPortalLoadingWidget = function () {
  fnGetPortalLoadingRefs()
  fnSyncPortalLoadingRootState()
  if (typeof window !== 'undefined') {
    window.PORTAL_LOADING_WIDGET = {
      startPortalLoadingTask: startPortalLoadingTask,
      removePortalLoadingTask: fnRemovePortalLoadingTask
    }
  }
}

export {
  initPortalLoadingWidget,
  startPortalLoadingTask
}
